import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronRight, Facebook } from "lucide-react";
import { Wordmark } from "@/components/Brand";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";

const FACEBOOK_URL = "https://www.facebook.com/share/1SYWj1kpQz/?mibextid=wwXIfr";

const NAV = [
  ["home", "nav.home"],
  ["events", "nav.events"],
  ["portfolio", "nav.portfolio"],
  ["about", "nav.about"],
  ["services", "nav.services"],
  ["clientGalleries", "nav.clientGalleries"],
  ["collection", "nav.collection"],
  ["restoration", "nav.restoration"],
  ["prints", "nav.prints"],
  ["guide", "nav.guide"],
  ["contact", "nav.contact"],
];

const LanguageToggle = ({ compact = false, scope = "desktop" }) => {
  const { lang, setLang } = useI18n();
  return (
    <div className={`flex items-center gap-1 ${compact ? "" : "ml-2"}`} aria-label="Language selector">
      {["es", "en"].map((code, i) => (
        <span key={code} className="flex items-center">
          {i === 1 && <span className="px-1 text-[#C69A3B]">|</span>}
          <button
            type="button"
            data-testid={scope === "desktop" ? `language-selector-${code}` : `language-selector-mobile-${code}`}
            onClick={() => setLang(code)}
            aria-current={lang === code}
            className={`label-mono min-h-[44px] px-1 transition-colors duration-300 ${
              lang === code ? "text-[#8F3329]" : "text-[#C69A3B] hover:text-[#8F3329]"
            }`}
          >
            {code.toUpperCase()}
          </button>
        </span>
      ))}
    </div>
  );
};

export default function Layout({ children }) {
  const { t, path } = useI18n();
  const { user, isAdmin, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  const linkClass = ({ isActive }) =>
    `text-sm tracking-wide transition-colors duration-300 border-b-2 py-1 ${
      isActive ? "text-[#8F3329] border-[#8F3329]" : "text-[#252525] border-transparent hover:text-[#8F3329]"
    }`;

  const navItems = (
    <>
      {NAV.map(([key, label]) => (
        <NavLink
          key={key}
          to={path(key)}
          end={key === "home"}
          data-testid={`nav-link-${key}`}
          className={linkClass}
        >
          {t(label)}
        </NavLink>
      ))}
      <NavLink to={path("account")} data-testid="nav-link-account" className={linkClass}>
        {t("nav.account")}
      </NavLink>
      {isAdmin && (
        <NavLink to={path("admin")} data-testid="nav-link-admin" className={linkClass}>
          {t("nav.admin")}
        </NavLink>
      )}
    </>
  );

  return (
    <div className="flex min-h-screen flex-col bg-[#F6F0E4] text-[#252525]">
      <header className="sticky top-0 z-40 border-b border-[#C69A3B]/30 bg-[#F6F0E4]/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-[1500px] items-center justify-between gap-6 px-4 py-4 sm:px-8 lg:px-12">
          <Wordmark />
          <nav className="hidden items-center gap-6 xl:flex" aria-label="Main">
            {navItems}
            <LanguageToggle />
          </nav>
          <div className="flex items-center gap-2 xl:hidden">
            <LanguageToggle compact scope="mobile" />
            <button
              type="button"
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? t("nav.close") : t("nav.menu")}
              className="grid h-11 w-11 place-items-center border border-[#C69A3B] text-[#8F3329]"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
        {open && (
          <nav
            data-testid="mobile-nav-panel"
            className="border-t border-[#C69A3B]/30 bg-[#FAFAF7] px-4 py-4 xl:hidden"
            aria-label="Mobile"
          >
            <div className="flex flex-col gap-1">
              {NAV.map(([key, label]) => (
                <Link
                  key={key}
                  to={path(key)}
                  data-testid={`mobile-nav-link-${key}`}
                  className="flex min-h-[48px] items-center justify-between border-b border-[#D8D3CB] text-base text-[#252525]"
                >
                  {t(label)} <ChevronRight size={16} className="text-[#C69A3B]" />
                </Link>
              ))}
              <Link
                to={path("account")}
                data-testid="mobile-nav-link-account"
                className="flex min-h-[48px] items-center justify-between border-b border-[#D8D3CB] text-base"
              >
                {t("nav.account")} <ChevronRight size={16} className="text-[#C69A3B]" />
              </Link>
              {isAdmin && (
                <Link
                  to={path("admin")}
                  data-testid="mobile-nav-link-admin"
                  className="flex min-h-[48px] items-center justify-between border-b border-[#D8D3CB] text-base"
                >
                  {t("nav.admin")} <ChevronRight size={16} className="text-[#C69A3B]" />
                </Link>
              )}
              {user && (
                <button
                  type="button"
                  data-testid="mobile-logout-btn"
                  onClick={logout}
                  className="mt-2 min-h-[48px] text-left text-sm text-[#8F3329]"
                >
                  {t("btn.logout")}
                </button>
              )}
            </div>
          </nav>
        )}
      </header>

      <main id="main" className="flex-1">{children}</main>

      <footer className="mt-24 border-t border-[#C69A3B]/40 bg-[#FAFAF7]">
        <div className="mx-auto grid max-w-[1500px] gap-10 px-4 py-14 sm:px-8 lg:grid-cols-3 lg:px-12">
          <div>
            <Wordmark />
            <p className="mt-5 max-w-sm text-sm text-[#5A5752]">{t("brand.positioning")}</p>
          </div>
          <nav className="grid grid-cols-2 gap-2 text-sm" aria-label="Footer">
            {NAV.map(([key, label]) => (
              <Link
                key={key}
                to={path(key)}
                data-testid={`footer-link-${key}`}
                className="py-1 text-[#252525] transition-colors duration-300 hover:text-[#8F3329]"
              >
                {t(label)}
              </Link>
            ))}
            <NavLink to={path("privacy")} data-testid="footer-privacy-link" className="py-1 text-[#5A5752] transition-colors duration-300 hover:text-[#8F3329]">
              {t("footer.privacy")}
            </NavLink>
          </nav>
          <div className="lg:text-right">
            <p className="label-mono">HR Creations</p>
            <p className="mt-2 font-serif-display text-xl text-[#8F3329]">{t("brand.tagline")}</p>
            <div className="mt-5 flex flex-col gap-2 text-sm lg:items-end">
              <a
                href="mailto:hrcreations90@gmail.com"
                data-testid="footer-email"
                className="text-[#252525] transition-colors duration-300 hover:text-[#8F3329]"
              >
                hrcreations90@gmail.com
              </a>
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-facebook"
                className="inline-flex items-center gap-2 text-[#252525] transition-colors duration-300 hover:text-[#8F3329] lg:justify-end"
              >
                <Facebook size={15} className="text-[#C69A3B]" /> Facebook
              </a>
            </div>
            <p className="mt-6 text-xs text-[#5A5752]" data-testid="footer-copyright">
              {t("footer.rights")}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
